import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { records, cleanApiBaseUrl } from '../lib/api';
import toast from 'react-hot-toast';
import { Upload, FileText, X, CheckCircle, Loader, AlertCircle, Clock } from 'lucide-react';

// Multi-file upload (todo #154).
//
// TRANSPORT: the client loops over the EXISTING single-file endpoint rather than
// posting a batch to a new multer.array() route. No backend change, per-file
// progress for free, one bad file cannot kill the batch, and no Railway
// request-size or timeout ceiling on a forty-file post. The costs, accepted: N
// auth checks and N audit rows -- though each record genuinely IS an upload
// event -- and closing the tab mid-batch leaves the remainder unsent, which is
// why there is an unload guard below.
//
// CAPPED, and the cap is stated rather than silently enforced. Not for the
// upload, which is cheap, but because of what follows it: ocr_queue is a SERIAL
// lane and a scanned page measures 10-38 SECONDS (Finding #90), on the single GPU
// that serves chat and embeddings for all six environments. Thirty scanned files
// is hours of shared GPU. The files land fast; the TEXT catches up, and if that
// is not visible it reads as broken -- hence the per-file status below.
const MAX_BATCH = 25;
// Three at a time. Higher does not help: the API is one process and the OCR that
// follows is serial anyway, so more concurrency only deepens the same queue.
const UPLOAD_CONCURRENCY = 3;

// Bounded worker pool. Deliberately not Promise.all over everything: 25
// simultaneous multipart posts would compete with the very requests the page
// needs to stay responsive.
async function runPool(items, limit, worker) {
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const i = next;
        next += 1;
        await worker(items[i], i);
      }
    })
  );
}

const titleFromFilename = (name) => name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ').trim();

const BASE_DOC_TYPES = ['Resolution','Ordinance','Meeting Minutes','Memorandum','Map','Contract','Report'];
const CLINICAL_DOC_TYPES = ['Clinical Note','Summary Template','Lab Report','Imaging Report','Discharge Summary'];
const OTHER_DOC_TYPE = 'Other';
// Used ONLY until /api/config answers, and if it never answers. The server owns
// the real list (middleware/security.js UPLOAD_FORMATS) and serves it as
// upload_formats — a fourth hand-maintained copy of this list is exactly how .txt
// became unselectable (Finding #66) and how three lists came to disagree
// (Finding #119). Deliberately CONSERVATIVE: the formats that have always worked,
// so a config failure narrows what can be selected rather than letting through
// something the server will reject.
const FALLBACK_ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tif', '.tiff'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
};

const BASE_DEPARTMENTS = ['City Clerk','City Attorney','Information Technology','Public Works','Planning','Finance','Parks & Recreation','Police','Fire','City Manager'];
const CLINICAL_DEPARTMENTS = ['Pain Management','Orthopedics','Primary Care','Physical Therapy','Radiology','Case Management / Utilization Review'];

export default function UploadPage() {
  const navigate = useNavigate();
  // An ARRAY now, but the single-file path is unchanged: one file still renders
  // the full metadata form with a title, description and tags. Batch mode starts
  // at two, because per-file titles cannot be typed and must come from filenames.
  const [files, setFiles] = useState([]);
  // Per-file outcome, keyed by index: { state, ocr_status, error }.
  const [results, setResults] = useState({});
  const [batchDone, setBatchDone] = useState(false);
  // Mirrors `results` so the end-of-batch check reads what actually happened.
  // Reading the state variable there would see a stale value: the per-file
  // setResults calls are queued, not applied, by the time the pool drains.
  const resultsRef = useRef({});
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState('');
  const [medicalMode, setMedicalMode] = useState(false);
  const [acceptMap, setAcceptMap] = useState(FALLBACK_ACCEPT);
  const [form, setForm] = useState({
    title: '', document_type: '', department: '', description: '', record_date: '', tags: '', upload_kind: 'record'
  });

  // Clinical Summary Generator (Plan #45) is dev.cognivault.us (DOI) only — off everywhere else by default.
  useEffect(() => {
    fetch(`${cleanApiBaseUrl()}/api/config`)
      .then(r => r.json())
      .then(cfg => {
        setMedicalMode(!!cfg.medical_mode);
        // react-dropzone wants { mime: [ext, ...] }, the server sends
        // [{ ext, mimes }] — invert it. One MIME can carry several extensions
        // (image/jpeg → .jpg/.jpeg), so entries accumulate rather than overwrite.
        if (Array.isArray(cfg.upload_formats) && cfg.upload_formats.length) {
          const map = {};
          for (const f of cfg.upload_formats) {
            for (const mime of f.mimes || []) {
              if (!map[mime]) map[mime] = [];
              if (!map[mime].includes(f.ext)) map[mime].push(f.ext);
            }
          }
          setAcceptMap(map);
        }
      })
      .catch(() => {});
  }, []);

  const DOC_TYPES = medicalMode ? [...BASE_DOC_TYPES, ...CLINICAL_DOC_TYPES, OTHER_DOC_TYPE] : [...BASE_DOC_TYPES, OTHER_DOC_TYPE];
  const DEPARTMENTS = medicalMode ? [...BASE_DEPARTMENTS, ...CLINICAL_DEPARTMENTS] : BASE_DEPARTMENTS;

  // ADDS to the selection rather than replacing it. Replacing was the first
  // behaviour and it was wrong: a batch is very often assembled from more than one
  // drop or more than one folder, and silently discarding the previous three files
  // when two more arrive is indistinguishable from losing them.
  //
  // Deduplicated on name + size + lastModified. Without it, dropping the same file
  // twice would create two records holding identical text -- which is exactly the
  // duplicate-record problem in Finding #126, where each duplicate then costs a
  // retrieval candidate slot.
  const onDrop = useCallback((accepted) => {
    if (!accepted.length || uploading) return;

    const key = f => `${f.name}|${f.size}|${f.lastModified}`;
    const already = new Set(files.map(key));
    const fresh = accepted.filter(f => !already.has(key(f)));
    const duplicates = accepted.length - fresh.length;
    // The cap applies to the COMBINED total, not to this drop, or three drops of
    // twenty would sail past it.
    const room = Math.max(0, MAX_BATCH - files.length);
    const added = fresh.slice(0, room);
    const overflow = fresh.length - added.length;

    // Both messages are computed OUT here rather than inside a setState updater:
    // React may invoke an updater twice in development, which would double the
    // toasts.
    if (duplicates) toast(`${duplicates} file${duplicates === 1 ? '' : 's'} already in the list`);
    if (overflow) {
      toast.error(`${overflow} more file${overflow === 1 ? '' : 's'} not added — the limit is ${MAX_BATCH} per batch. Upload the rest separately.`,
        { duration: 7000 });
    }
    if (!added.length) return;

    const next = [...files, ...added];
    setFiles(next);
    // A previous batch's results no longer describe this selection.
    setResultsBoth({});
    setBatchDone(false);
    // Only prefill a title for a SINGLE file. In a batch each record takes its
    // title from its own filename, so one shared title would be wrong for all
    // but the first.
    if (next.length === 1 && !form.title) {
      setForm(f => ({ ...f, title: titleFromFilename(added[0].name) }));
    }
  }, [files, form.title, uploading]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true,
    // Built from what the SERVER allows (see FALLBACK_ACCEPT above). This map used
    // to be maintained by hand here, which is why Excel could not even be
    // SELECTED while the server would happily have stored it.
    accept: acceptMap
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Every write to `results` goes through here so the ref cannot drift from the
  // state. Two sources of truth for the same thing is how the end-of-batch summary
  // would come to disagree with the rows on screen.
  const setResultsBoth = (updater) => {
    setResults(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      resultsRef.current = next;
      return next;
    });
  };

  // Closing the tab mid-batch abandons whatever has not been posted yet. The
  // records already created are fine; the rest simply never happen, so warn
  // rather than let it look like the whole batch went through.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  // One file's worth of work, shared by both paths so they cannot drift apart.
  // `title` is passed in because a batch derives it per file while a single upload
  // takes whatever was typed.
  const uploadOne = async (f, title, { includeDescriptive }) => {
    const fd = new FormData();
    fd.append('file', f);
    fd.append('title', title);
    fd.append('document_type', form.document_type);
    if (form.department) fd.append('department', form.department);
    if (form.record_date) fd.append('record_date', form.record_date);
    if (form.tags) fd.append('tags', form.tags);
    // Description belongs to ONE document. Copying the same sentence onto twenty
    // records would be worse than leaving it blank, and Phase 4's auto_summary
    // fills that gap per record anyway.
    if (includeDescriptive && form.description) fd.append('description', form.description);
    // A batch of summary templates is not a real case, so batch mode always
    // uploads records. The selector is hidden in that mode for the same reason.
    fd.append('is_template', includeDescriptive && form.upload_kind === 'template' ? 'true' : 'false');
    const { data } = await records.upload(fd);
    return data;
  };

  const submitSingle = async () => {
    if (!form.title || !form.document_type) return toast.error('Title and document type are required');
    setUploading(true);
    setStage('Uploading and extracting text...');
    try {
      const data = await uploadOne(files[0], form.title, { includeDescriptive: true });
      // Scans and images are OCR'd in the background — the record exists and is
      // saved, but its text is not there yet. Saying "uploaded" and nothing else
      // would leave the user staring at a record with no text and no explanation.
      if (data.ocr_status === 'pending') {
        toast.success('Record uploaded — reading the document in the background. Text appears shortly.', { duration: 6000 });
      } else if (data.ocr_status === 'empty') {
        toast.success('Record uploaded, but no text could be extracted from this file.', { duration: 6000 });
      } else {
        toast.success(`Record uploaded${data.ocr_extracted ? ' with text extracted' : ''}`);
      }
      navigate('/records');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setStage('');
    }
  };

  const submitBatch = async () => {
    if (!form.document_type) return toast.error('Document type is required for the batch');
    setUploading(true);
    setBatchDone(false);
    setResultsBoth(Object.fromEntries(files.map((_, i) => [i, { state: 'queued' }])));

    let done = 0;
    await runPool(files, UPLOAD_CONCURRENCY, async (f, i) => {
      setResultsBoth(r => ({ ...r, [i]: { state: 'uploading' } }));
      try {
        const data = await uploadOne(f, titleFromFilename(f.name), { includeDescriptive: false });
        setResultsBoth(r => ({ ...r, [i]: { state: 'done', ocr_status: data.ocr_status } }));
      } catch (err) {
        // ONE FAILURE MUST NOT FAIL THE BATCH. The reason is kept per file --
        // a 400 from the upload guard names the rejected type, and that is the
        // most useful thing the row can say.
        setResultsBoth(r => ({ ...r, [i]: { state: 'failed', error: err.response?.data?.error || 'Upload failed' } }));
      } finally {
        done += 1;
        setStage(`Uploaded ${done} of ${files.length}...`);
      }
    });

    setUploading(false);
    setStage('');

    // Read the outcome from the state we just built rather than from `results`,
    // which the setState calls above may not have flushed yet.
    const outcome = files.map((_, i) => resultsRef.current[i] || { state: 'failed' });
    const failed = outcome.filter(r => r.state === 'failed').length;
    const pending = outcome.filter(r => r.state === 'done' && r.ocr_status === 'pending').length;

    if (!failed) {
      // A CLEAN RUN NAVIGATES, exactly as a single upload does. Staying put was a
      // failure-case rule applied unconditionally: with nothing to inspect it just
      // traps the user on a form they have finished with. The toast carries the one
      // thing /records does not show -- how many are still having text extracted.
      toast.success(
        `${outcome.length} record${outcome.length === 1 ? '' : 's'} uploaded` +
        (pending ? ` — ${pending} still extracting text in the background` : ''),
        { duration: pending ? 7000 : 4000 }
      );
      setFiles([]);
      setResultsBoth({});
      navigate('/records');
      return;
    }

    // PARTIAL FAILURE: stay, and keep ONLY the files that failed. Leaving the
    // successful ones in the list would re-upload them on the next click and
    // create duplicate records (Finding #126). What remains is a retry list.
    const keep = [];
    const keptResults = {};
    files.forEach((f, i) => {
      if (outcome[i].state !== 'failed') return;
      keptResults[keep.length] = outcome[i];
      keep.push(f);
    });
    setFiles(keep);
    setResultsBoth(keptResults);
    setBatchDone(true);
    toast.error(`${outcome.length - failed} of ${outcome.length} uploaded — ${failed} failed and remain listed for retry`,
      { duration: 8000 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error('Please select at least one file');
    return files.length === 1 ? submitSingle() : submitBatch();
  };

  const fmtSize = (bytes) => bytes < 1e6 ? `${(bytes/1e3).toFixed(0)} KB` : `${(bytes/1e6).toFixed(1)} MB`;

  const isBatch = files.length > 1;
  const removeAt = (i) => { setFiles(fs => fs.filter((_, j) => j !== i)); setResultsBoth({}); setBatchDone(false); };

  // What a finished row means. 'pending' is the one worth spelling out: after the
  // upload guard and the parsers landed, spreadsheets, CSV, JSON, DOCX, TXT and
  // native PDFs come back 'done' immediately because they are PARSED inline, while
  // images and scanned PDFs go to the serial GPU lane. A row that says "extracting"
  // is not stuck, it is queued behind up to 38 seconds per page.
  const rowNote = (res) => {
    if (!res) return null;
    if (res.state === 'queued') return 'waiting';
    if (res.state === 'uploading') return 'uploading';
    if (res.state === 'failed') return res.error;
    if (res.ocr_status === 'pending') return 'uploaded — extracting text in the background';
    if (res.ocr_status === 'empty') return 'uploaded — no text could be extracted';
    return 'uploaded — text ready';
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Upload record</h1>
        <p className="text-sm text-gray-500 mt-1">One file or many — text extraction runs automatically, and scans are read in the background</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Dropzone */}
        <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragActive ? 'border-blue-400 bg-blue-50' : files.length ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
          <input {...getInputProps()} />
          {files.length ? (
            <div className="space-y-1.5 text-left" onClick={(e) => e.stopPropagation()}>
              {files.map((f, i) => {
                const res = results[i];
                const note = rowNote(res);
                return (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-3 bg-white/70 rounded-lg px-3 py-2">
                    {res?.state === 'failed' ? <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                      : res?.state === 'uploading' ? <Loader size={16} className="text-blue-500 animate-spin flex-shrink-0" />
                      : res?.state === 'queued' ? <Clock size={16} className="text-gray-300 flex-shrink-0" />
                      : <CheckCircle size={16} className={`flex-shrink-0 ${res?.state === 'done' ? 'text-green-500' : 'text-gray-300'}`} />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 truncate">{f.name}</div>
                      <div className={`text-xs truncate ${res?.state === 'failed' ? 'text-red-500' : 'text-gray-500'}`}>
                        {fmtSize(f.size)}{note ? ` · ${note}` : ''}
                      </div>
                    </div>
                    {!uploading && (
                      <button type="button" onClick={() => removeAt(i)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={15} /></button>
                    )}
                  </div>
                );
              })}
              {isBatch && !uploading && !batchDone && (
                <div className="text-xs text-gray-500 pt-1.5">
                  {files.length} files · each record is titled from its filename
                </div>
              )}
            </div>
          ) : (
            <div>
              <Upload size={24} className="mx-auto text-gray-300 mb-3" />
              <div className="text-sm font-medium text-gray-600">{isDragActive ? 'Drop them here' : 'Drag & drop or click to select'}</div>
              <div className="text-xs text-gray-400 mt-1">One file, or up to {MAX_BATCH} at once · PDF, images, DOC/DOCX, TXT, XLSX, CSV, JSON up to 50MB each</div>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-700">
            {isBatch ? `Metadata applied to all ${files.length} records` : 'Record metadata'}
          </h2>
          {isBatch && (
            /* Says what is shared and what is not, because a batch silently
               copying one description onto twenty records would be worse than
               leaving it blank. Document type accepts 'Other' deliberately:
               Phase 4's categorisation auto-applies its own guess when the field
               is NULL or 'Other', so 'Other' means "let the model decide". */
            <div className="flex items-start gap-3 bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
              <FileText size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                Each record is titled from its own filename. The type, department, date and tags below
                apply to every file. Choose <span className="font-medium">Other</span> as the type to let
                automatic categorisation decide per document. Per-record description and summary templates
                are single-file only.
              </div>
            </div>
          )}
          {medicalMode && !isBatch && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">What are you uploading? <span className="text-red-400">*</span></label>
              <select value={form.upload_kind} onChange={e => set('upload_kind', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                <option value="record">A record (e.g. a clinical note — stored, searched, and summarizable)</option>
                <option value="template">A summary template (defines the section order for "Generate Summary")</option>
              </select>
            </div>
          )}
          {!isBatch && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Title <span className="text-red-400">*</span></label>
              <input value={form.title} onChange={e => set('title', e.target.value)} required
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50"
                placeholder="e.g. Resolution 2024-045 — Budget Amendment" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Document type <span className="text-red-400">*</span></label>
              <select value={form.document_type} onChange={e => set('document_type', e.target.value)} required
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                <option value="">Select type...</option>
                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Department</label>
              <select value={form.department} onChange={e => set('department', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Record date</label>
              <input type="date" value={form.record_date} onChange={e => set('record_date', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400"
                placeholder="budget, 2024, amendment" />
            </div>
          </div>
          {!isBatch && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 resize-none"
                placeholder="Brief description of this record..." />
            </div>
          )}
        </div>

        {/* OCR notice */}
        <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
          <FileText size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">OCR runs automatically.</span> Full text will be extracted from PDFs and images and indexed for search. Pre-processed documents are accepted without reprocessing.
          </div>
        </div>

        {/* Submit */}
        {uploading && (
          <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-xl p-4">
            <Loader size={16} className="animate-spin text-blue-500" />
            <span>{stage}</span>
          </div>
        )}

        {/* A finished batch does NOT navigate away. A partial failure has to stay
            on screen to be read -- bouncing to /records would hide exactly the
            rows the user needs to see. */}
        {batchDone && (() => {
          const list = Object.values(results);
          const ok = list.filter(r => r.state === 'done').length;
          const failed = list.filter(r => r.state === 'failed').length;
          const pending = list.filter(r => r.state === 'done' && r.ocr_status === 'pending').length;
          return (
            <div className={`rounded-xl p-4 text-sm ${failed ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
              <div className="font-medium">
                {ok} of {list.length} uploaded{failed ? `, ${failed} failed` : ''}
              </div>
              {pending > 0 && (
                <div className="text-xs mt-1">
                  {pending} still having text extracted in the background — the queue handles one document at a time,
                  and a scanned page takes 10–38 seconds, so a large batch can take a while. The records exist already.
                </div>
              )}
              {failed > 0 && <div className="text-xs mt-1">Failed files keep their reason in the list above.</div>}
            </div>
          );
        })()}

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/records')}
            className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            {batchDone ? 'Go to records' : 'Cancel'}
          </button>
          <button type="submit" disabled={uploading || !files.length || batchDone}
            className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? (stage || 'Processing...')
              : isBatch ? `Upload & index ${files.length} records`
              : 'Upload & index record'}
          </button>
        </div>
      </form>
    </div>
  );
}
